import type { ApiResponse } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('auth_token');
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
  const data: ApiResponse<T> = await response.json();
  
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Unknown error');
  }
  
  return data.data as T;
}

// ============================================
// TYPES
// ============================================

export type SubscriptionStatus = 'active' | 'cancelled' | 'expired' | 'pending';

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  priceMonthly: number;
  priceYearly: number;
  features: string[];
  limits: Record<string, number>;
  sortOrder: number;
  isCurrent: boolean;
  isUpgrade: boolean;
}

export interface Subscription {
  id: string;
  planId: string;
  status: SubscriptionStatus;
  startDate: string;
  endDate: string | null;
  autoRenew: boolean;
  plan: SubscriptionPlan;
  daysRemaining: number;
  isExpiringSoon: boolean;
}

export interface UserLimits {
  dailyActivities: number;
  skillSlots: number;
  guildMembership: number;
  eventCheckins: number;
  daemonMessages: number;
}

// ============================================
// API CALLS
// ============================================

export async function getSubscriptionPlans(): Promise<{ plans: SubscriptionPlan[] }> {
  return request('/subscriptions/plans');
}

export async function getCurrentSubscription(): Promise<{ subscription: Subscription | null }> {
  return request('/subscriptions/current');
}

export async function getUserLimits(): Promise<{ limits: UserLimits }> {
  return request('/subscriptions/limits');
}

export async function subscribe(
  planId: string,
  period: 'monthly' | 'yearly'
): Promise<{ subscription: Subscription; paymentUrl: string }> {
  return request('/subscriptions/subscribe', {
    method: 'POST',
    body: JSON.stringify({ planId, period }),
  });
}

export async function cancelAutoRenew(): Promise<{ message: string }> {
  return request('/subscriptions/cancel-renewal', {
    method: 'POST',
  });
}

export async function getSubscriptionHistory(): Promise<{
  history: Array<{ subscription: Subscription; plan: SubscriptionPlan }>;
}> {
  return request('/subscriptions/history');
}
